import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Flag, X, Camera, Loader2 } from 'lucide-react'
import { issuesAPI } from '@renderer/api/issues'
import { useUser } from '@renderer/context/UserContext'
import { useCurrentSession } from '@renderer/lib/currentSession'
import { closeReportIssue, openReportIssue, useReportIssueUi } from '@renderer/lib/reportIssue'

async function captureScreen(): Promise<string | null> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (!md?.getDisplayMedia) return null
  let stream: MediaStream | null = null
  try {
    stream = await md.getDisplayMedia({
      video: true,
      audio: false,
      preferCurrentTab: true
    } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean })
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    await video.play()
    await new Promise((r) => setTimeout(r, 150))
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  } catch {
    return null
  } finally {
    stream?.getTracks().forEach((t) => t.stop())
  }
}

function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'string' ? parseInt(v, 10) : (v as number)
  return Number.isFinite(n) ? (n as number) : null
}

export const ReportIssue: React.FC = () => {
  const { canReportIssues } = useUser()
  const session = useCurrentSession()

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const [isFs, setIsFs] = useState(false)
  useEffect(() => {
    const update = () => {
      const fsEl = (document.fullscreenElement as HTMLElement) ?? null
      setPortalTarget(fsEl ?? document.body)
      setIsFs(!!fsEl)
    }
    update()
    document.addEventListener('fullscreenchange', update)
    return () => document.removeEventListener('fullscreenchange', update)
  }, [])

  const { open } = useReportIssueUi()
  const setOpen = (next: boolean) => (next ? openReportIssue() : closeReportIssue())
  const [capturing, setCapturing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState<null | 'ok' | 'error'>(null)

  const doCapture = useCallback(async () => {
    setCapturing(true)
    await new Promise((r) => setTimeout(r, 80))
    const shot = await captureScreen()
    setScreenshot(shot)
    setCapturing(false)
  }, [])

  const openReporter = useCallback(async () => {
    setStatus(null)
    setDescription('')
    setCategory('')
    setScreenshot(null)
    setCapturing(true)
    setOpen(true)
    await new Promise((r) => setTimeout(r, 80))
    const shot = await captureScreen()
    setScreenshot(shot)
    setCapturing(false)
  }, [])

  const close = useCallback(() => {
    if (!submitting) setOpen(false)
  }, [submitting])

  const submit = useCallback(async () => {
    setSubmitting(true)
    setStatus(null)
    try {
      const res = await issuesAPI.report({
        session_id: session.sessionId ?? null,
        description: description.trim() || null,
        category: category || null,
        screenshot_base64: screenshot,
        course_id: toNum(session.courseId),
        module_id: toNum(session.moduleId),
        concept_id: toNum(session.conceptId),
        page_url: typeof window !== 'undefined' ? window.location.href : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        client_timestamp: new Date().toISOString()
      })
      if (!res.ok) throw new Error(String(res.status))
      setStatus('ok')
      setTimeout(() => setOpen(false), 900)
    } catch {
      setStatus('error')
    } finally {
      setSubmitting(false)
    }
  }, [session, description, category, screenshot])

  if (!canReportIssues || !portalTarget) return null

  return createPortal(
    <>
      <button
        onClick={openReporter}
        title="Report an issue"
        className={`hidden md:flex fixed ${isFs ? 'bottom-24' : 'bottom-4'} right-4 z-[9998] items-center gap-2 px-3.5 h-10 rounded-full bg-black/70 backdrop-blur-lg border border-white/15 text-white/90 hover:text-white hover:bg-black/85 shadow-lg cursor-pointer transition-all`}
      >
        <Flag size={16} className="text-amber-400" />
        <span className="text-sm font-medium">Report issue</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-opacity duration-150"
          style={{ pointerEvents: capturing ? 'none' : 'auto', opacity: capturing ? 0 : 1 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close()
          }}
        >
          <div className="w-full max-w-md rounded-2xl bg-neutral-900 border border-white/10 shadow-2xl text-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2 font-semibold">
                <Flag size={18} className="text-amber-400" /> Report an issue
              </div>
              <button
                onClick={close}
                className="text-white/60 hover:text-white transition-colors"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {!session.sessionId && (
                <div className="text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  You&apos;re not in a live lab session, so this report won&apos;t be linked to a
                  lab session trace &mdash; it still captures the screenshot, page, and your notes.
                </div>
              )}

              <div>
                <label className="text-xs text-white/60">What went wrong?</label>
                <textarea
                  autoFocus
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what happened - what the tutor said, what you expected..."
                  rows={4}
                  className="mt-1 w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/30 resize-none"
                />
              </div>

              <div>
                <label className="text-xs text-white/60">Type</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/30"
                >
                  <option value="">Unspecified</option>
                  <option value="accuracy">Wrong / inaccurate answer</option>
                  <option value="retrieval">Couldn&apos;t find lab material</option>
                  <option value="lab">Lab environment / RDP problem</option>
                  <option value="voice">Voice / audio problem</option>
                  <option value="stuck">Got stuck / stalled</option>
                  <option value="ui">UI / page problem</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-white/60">Screenshot</label>
                  <button
                    onClick={doCapture}
                    className="flex items-center gap-1 text-xs text-white/70 hover:text-white transition-colors"
                  >
                    <Camera size={14} /> {screenshot ? 'Retake' : 'Capture'}
                  </button>
                </div>
                <div className="mt-1 rounded-lg border border-white/10 bg-black/40 h-32 flex items-center justify-center overflow-hidden">
                  {screenshot ? (
                    <img
                      src={screenshot}
                      alt="captured screenshot"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-white/40">No screenshot captured</span>
                  )}
                </div>
              </div>

              <p className="text-[11px] text-white/40 leading-relaxed">
                This session&apos;s id and recent backend logs are attached automatically to help
                us debug.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
              {status === 'ok' && (
                <span className="text-xs text-emerald-400 mr-auto">Thanks &mdash; reported!</span>
              )}
              {status === 'error' && (
                <span className="text-xs text-red-400 mr-auto">Failed to send. Try again.</span>
              )}
              <button
                onClick={close}
                className="px-3 h-9 rounded-full text-sm text-white/70 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting || (!description.trim() && !screenshot)}
                className="flex items-center gap-1.5 px-4 h-9 rounded-full bg-amber-500/90 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold text-sm transition-colors"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                Submit report
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    portalTarget
  )
}

export default ReportIssue
